'use client';
import { DEFAULT_SAMPLE_COUNT } from '@/app/constants';
import type { ImagePrompt, TemplateSettings } from '@/app/types';
import { checkSourceImageSize, createImage, getImageSize } from '@/app/utils';
import { getScaledImageSize } from '@/app/utils/client';
import type { SupportedSettings, TemplateData } from './types';

interface ImageDimensions {
  width: number;
  height: number;
}

const HEIGHT_RATIO_THRESHOLD = 2;

export const getScaledImgSize = (img1: HTMLImageElement, img2: HTMLImageElement) => {
  const [img1Height, img2Height] = [img1.height, img2.height];

  const heightRatio = Math.max(img1Height, img2Height) / Math.min(img1Height, img2Height);
  const targetHeight =
    heightRatio <= HEIGHT_RATIO_THRESHOLD
      ? Math.floor((img1Height + img2Height) / 2)
      : Math.min(img1Height, img2Height);

  const img1Scale = targetHeight / img1Height;
  const img2Scale = targetHeight / img2Height;

  return {
    img1: {
      width: Math.floor(img1.width * img1Scale),
      height: targetHeight,
    },
    img2: {
      width: Math.floor(img2.width * img2Scale),
      height: targetHeight,
    },
  };
};

export const getOriginalAndScaledImgs = async (imageUrl1: string, imageUrl2: string) => {
  const [img1, img2] = await Promise.all([createImage(imageUrl1), createImage(imageUrl2)]);
  const dimensions =
    img1.height !== img2.height
      ? getScaledImgSize(img1, img2)
      : {
          img1: {
            width: img1.width,
            height: img1.height,
          },
          img2: {
            width: img2.width,
            height: img2.height,
          },
        };
  return {
    originalImgElements: { img1, img2 }, // 源图像对象
    scaledImgSizes: dimensions, // 缩放后的图片宽高
  };
};

export const getMergedAndResizedImgHeight = (
  scaledImgSizes: { img1: ImageDimensions; img2: ImageDimensions },
  maxImageSize: number,
) => {
  const { img1: scaledImg1, img2: scaledImg2 } = scaledImgSizes;

  const mergedImgWidth = scaledImg1.width + scaledImg2.width;
  const mergedImgHeight = Math.max(scaledImg1.height, scaledImg2.height);

  // 获取图片1、2merge后再resized后的高度
  let resizedImgHeight = mergedImgHeight;
  if (mergedImgWidth * mergedImgHeight > maxImageSize) {
    const { targetHeight } = getScaledImageSize(mergedImgWidth, mergedImgHeight, maxImageSize);
    resizedImgHeight = targetHeight;
  }
  return resizedImgHeight;
};

export const mergeImage = (
  originalImgs: { img1: HTMLImageElement; img2: HTMLImageElement },
  targetSizes: { img1: ImageDimensions; img2: ImageDimensions },
): string => {
  const { img1: originalImg1, img2: originalImg2 } = originalImgs;
  const { img1: targetImg1, img2: targetImg2 } = targetSizes;
  const canvasWidth = targetImg1.width + targetImg2.width;
  const canvasHeight = Math.max(targetImg1.height, targetImg2.height);

  // 创建 Canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx || !canvas) {
    throw new Error('getCanvasContextError');
  }
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // 计算垂直居中的偏移量
  const offsetY1 = (canvasHeight - targetImg1.height) / 2; // 第一张图片的垂直偏移
  const offsetY2 = (canvasHeight - targetImg2.height) / 2; // 第二张图片的垂直偏移

  // 绘制图片
  ctx.drawImage(
    originalImg1, // 源图像对象
    0, // 源图像裁剪的起始 X 坐标
    0, // 源图像裁剪的起始 Y 坐标
    originalImg1.width, // 源图像的宽度
    originalImg1.height, // 源图像的高度
    0, // 目标图像的 X 坐标
    offsetY1, // 目标图像的 Y 坐标
    targetImg1.width, // 目标图像的宽度
    targetImg1.height, // 目标图像的高度
  );
  ctx.drawImage(
    originalImg2,
    0,
    0,
    originalImg2.width,
    originalImg2.height,
    targetImg1.width,
    offsetY2,
    targetImg2.width,
    targetImg2.height,
  );

  return canvas.toDataURL('image/webp');
};

// 确保图片有尺寸信息
export const ensureImageSize = async (imagePrompt: ImagePrompt): Promise<boolean> => {
  if (checkSourceImageSize(imagePrompt)) return true;
  try {
    const { width, height } = await getImageSize(imagePrompt.sourceImagePreview);
    imagePrompt.sourceImageWidth = width;
    imagePrompt.sourceImageHeight = height;
    return true;
  } catch {
    return false;
  }
};

export const getSupportedSettingsFromTemplate = (data: TemplateData): SupportedSettings => {
  return {
    sampleCount: Boolean(data.supported_settings?.multiple_outputs),
    aspectRatio: data.supported_settings?.aspect_ratios || false,
    hasAudio: Boolean(data.default_settings && 'has_audio' in data.default_settings),
  };
};

// 工具方法：supported.aspectRatio 可能为 false 或 string[]
const getAspectRatioOptions = (supported: SupportedSettings): string[] | undefined => {
  return supported.aspectRatio ? supported.aspectRatio : undefined;
};

const getDefaultTemplateSettings = (
  defaultSettings: TemplateData['default_settings'],
  aspectRatioOptions?: string[],
): TemplateSettings => {
  const defaultAspectRatio = aspectRatioOptions
    ? defaultSettings?.aspect_ratio && aspectRatioOptions.includes(defaultSettings.aspect_ratio)
      ? defaultSettings.aspect_ratio
      : aspectRatioOptions[0]
    : undefined;

  return {
    sample_count: DEFAULT_SAMPLE_COUNT,
    aspect_ratio: defaultAspectRatio,
    has_audio: defaultSettings?.has_audio ?? false,
  };
};

// 工具方法：根据新模板能力清理现有设置，如果值无效则回退为模板可用值
export const sanitizeTemplateSettings = (
  current: TemplateSettings | undefined,
  supported: SupportedSettings,
  defaultSettings: TemplateData['default_settings'],
): TemplateSettings | undefined => {
  const aspectRatioOptions = getAspectRatioOptions(supported);
  const defaults = getDefaultTemplateSettings(defaultSettings, aspectRatioOptions);

  const next: TemplateSettings = {};

  // sample_count
  if (supported.sampleCount) {
    next.sample_count = current?.sample_count ?? defaults.sample_count;
  }

  // aspect_ratio
  if (aspectRatioOptions) {
    if (current?.aspect_ratio && aspectRatioOptions.includes(current.aspect_ratio)) {
      next.aspect_ratio = current.aspect_ratio;
    } else {
      next.aspect_ratio = defaults.aspect_ratio;
    }
  }

  // has_audio
  if (supported.hasAudio) {
    next.has_audio = current?.has_audio ?? defaults.has_audio;
  }

  return next;
};
